import {
  CSSProperties,
  LegacyRef,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useContext
} from 'react';
import cc from 'classcat';
import { RowContext } from '../Row/RowContext';
import './index.less';

type ColProps = {
  span?: number;
  flex?: string;
  offset?: number;
  pull?: number;
  push?: number;
  order?: number;
  style?: CSSProperties;
  children?: ReactNode;
  className?: string;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
  dir?: string;
  ref?: LegacyRef<HTMLDivElement>;
  id?: string;
  onClick?: (e: SyntheticEvent) => void;
  shouldApplySpecialWidth?: boolean;
  horizontalAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'center';
};

const ROW_WIDTH = 100;
const COL_MAX_COUNT = 24;
const specialWidths = {
  10: '40%',
  4: '20%'
};

export const Col = ({
  span,
  flex,
  offset,
  pull,
  push,
  order,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  style,
  children,
  className,
  dir,
  ref,
  id,
  onClick,
  shouldApplySpecialWidth,
  horizontalAlign,
  verticalAlign
}: ColProps): ReactElement => {
  const { gutter } = useContext(RowContext);

  const normalizedGutter = gutter
    ? gutter.every(g => g !== undefined)
      ? gutter
      : null
    : null;

  const colPaddings = normalizedGutter && {
    paddingLeft: normalizedGutter[0] / 2,
    paddingRight: normalizedGutter[0] / 2
  };

  const getColWidth = (span: number, customWidths?: Record<number, string>) => {
    if (customWidths && customWidths[span]) {
      return customWidths[span];
    }

    return `${(span * ROW_WIDTH) / COL_MAX_COUNT}%`;
  };

  const colStyle = {
    flex:
      flex ||
      (span
        ? `0 0 ${getColWidth(
            span,
            shouldApplySpecialWidth ? specialWidths : undefined
          )}`
        : undefined),
    maxWidth: span
      ? getColWidth(span, shouldApplySpecialWidth ? specialWidths : undefined)
      : undefined,
    display: flex,
    ...(gutter ? colPaddings : {}),
    ...(horizontalAlign
      ? { display: 'flex', justifyContent: horizontalAlign }
      : {}),
    ...(verticalAlign ? { display: 'flex', alignItems: verticalAlign } : {}),
    ...style
  } as CSSProperties;

  return (
    <div
      className={cc([
        'custom__col',
        {
          [`col__push--${push}`]: push,
          [`col__pull--${pull}`]: pull,
          [`col__order--${order}`]: order,
          [`col__offset--${offset}`]: offset,
          [`col__xs--${xs}`]: xs,
          [`col__sm--${sm}`]: sm,
          [`col__md--${md}`]: md,
          [`col__lg--${lg}`]: lg,
          [`col__xl--${xl}`]: xl,
          [`col__xxl--${xxl}`]: xxl,
          [`${className}`]: className
        }
      ])}
      onClick={onClick}
      style={colStyle}
      dir={dir}
      ref={ref}
      id={id}
    >
      {children}
    </div>
  );
};
